import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { faPencil } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FlyOutMenu from '../../../../core/components/FlyOutMenu';
import Button from '../../../../core/components/Button';
import Dialog from '../../../../core/components/Dialog';
import utilSvc from '../../../../core/utilSvc';
import datamonitorRepo, {
    MonitorHistoryItem,
    MonitorConfigDetail,
    MonitorDefaultSaveModel,
    defaultsUseLookback,
} from '../../data/datamonitorRepo';
import MonitorStatusIcon from './MonitorStatusIcon';
import { DEFAULT_MONITOR_CONFIG_FORM, isMonitorConfigFormDirty, MonitorConfigFormState } from './monitorConfigForm';

const INTERVAL_TYPES = [
    { label: 'DAYS', value: 'DAY' },
    { label: 'HOURS', value: 'HOUR' },
    { label: 'MINUTES', value: 'MINUTE' },
    { label: 'MONTHS', value: 'MONTH' },
];

type MonitorConfigEditFlyOutProps = {
    monitorId: string | null;
    monitorType: string;
    title: string;
    canEdit: boolean;
    onClose: () => void;
    onSaved: () => void;
    ignoreClickRef?: React.RefObject<HTMLElement>;
};

// legacy: interval/lookback must be whole numbers between 1 and 1,000,000
function invalidInteger(valStr: string): boolean {
    const n = Number(valStr);
    return !Number.isInteger(n) || n < 1 || n > 1000000;
}

const formatDate = (dateStr: string): string => {
    if (!dateStr) return '';
    try {
        return new Date(dateStr).toLocaleString('en-US', {
            month: 'numeric',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            second: '2-digit',
            hour12: true,
        });
    } catch {
        return dateStr;
    }
};

const MonitorConfigEditFlyOut = ({
    monitorId,
    monitorType,
    title,
    canEdit,
    onClose,
    onSaved,
    ignoreClickRef,
}: MonitorConfigEditFlyOutProps): JSX.Element => {
    const [history, setHistory] = useState<MonitorHistoryItem[]>([]);
    const [config, setConfig] = useState<MonitorConfigDetail | null>(null);
    const [loading, setLoading] = useState(false);
    const requestIdRef = useRef(0);

    const [form, setForm] = useState<MonitorConfigFormState>(DEFAULT_MONITOR_CONFIG_FORM);
    const [initialForm, setInitialForm] = useState<MonitorConfigFormState>(DEFAULT_MONITOR_CONFIG_FORM);
    const [isEditing, setIsEditing] = useState(false);
    const [intervalErr, setIntervalErr] = useState('');
    const [lookbackErr, setLookbackErr] = useState('');
    const [dialogMsg, setDialogMsg] = useState('');
    const [saveSuccessMsg, setSaveSuccessMsg] = useState('');
    const [saving, setSaving] = useState(false);
    const saveMessageTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const showLookback = !!config && defaultsUseLookback(config.ConfigName);

    useEffect(() => {
        if (!monitorId) {
            setHistory([]);
            setConfig(null);
            setLoading(false);
            setIsEditing(false);
            setSaveSuccessMsg('');
            return;
        }
        const currentRequestId = ++requestIdRef.current;
        setLoading(true);
        setHistory([]);
        setConfig(null);
        setIntervalErr('');
        setLookbackErr('');
        setDialogMsg('');
        setSaveSuccessMsg('');

        const load = async (): Promise<void> => {
            try {
                const items = await datamonitorRepo.getVerityHistory(monitorId);
                if (currentRequestId !== requestIdRef.current) return;
                setHistory(items);
            } catch {
                if (currentRequestId !== requestIdRef.current) return;
                setHistory([]);
            }
            try {
                const cfg = await datamonitorRepo.getDatafeedConfig(monitorId);
                if (currentRequestId !== requestIdRef.current) return;
                setConfig(cfg);
                const nextForm: MonitorConfigFormState = {
                    active: cfg.Active ? 'YES' : 'NO',
                    alert: cfg.Alert ? 'ON' : 'OFF',
                    intervalVal: String(cfg.IntervalVal ?? ''),
                    intervalType: cfg.IntervalType || 'DAY',
                    lookbackVal: String(cfg.LookbackVal ?? ''),
                    lookbackType: cfg.LookbackType || 'DAY',
                };
                setForm(nextForm);
                setInitialForm(nextForm);
                setIsEditing(false);
            } catch {
                // config optional
            }
            if (currentRequestId !== requestIdRef.current) return;
            setLoading(false);
        };
        load();

        return (): void => {
            // invalidate this run's in-flight requests using the captured id
            requestIdRef.current = currentRequestId + 1;
        };
    }, [monitorId]);

    const handleClose = useCallback((): void => {
        requestIdRef.current++;
        if (saveMessageTimerRef.current) {
            clearTimeout(saveMessageTimerRef.current);
            saveMessageTimerRef.current = null;
        }
        setLoading(false);
        onClose();
    }, [onClose]);

    const update = useCallback(
        <K extends keyof MonitorConfigFormState>(key: K, val: MonitorConfigFormState[K]): void => {
            setForm(prev => ({ ...prev, [key]: val }));
        },
        [],
    );

    const isDirty = useMemo(() => isMonitorConfigFormDirty(form, initialForm), [form, initialForm]);

    const handleEdit = useCallback((): void => {
        setSaveSuccessMsg('');
        setIsEditing(true);
    }, []);

    const handleCancelEdit = useCallback((): void => {
        setForm(initialForm);
        setIntervalErr('');
        setLookbackErr('');
        setSaveSuccessMsg('');
        setIsEditing(false);
    }, [initialForm]);

    const handleSave = useCallback(async (): Promise<void> => {
        if (!config || saving || !isDirty) return;

        let intMsg = '';
        let lookMsg = '';
        if (invalidInteger(form.intervalVal)) {
            intMsg = 'enter a whole number 1 - 1,000,000';
        } else if (!form.intervalType) {
            intMsg = 'required';
        }
        if (showLookback) {
            if (invalidInteger(form.lookbackVal)) {
                lookMsg = 'enter a whole number 1 - 1,000,000';
            } else if (!form.lookbackType) {
                lookMsg = 'required';
            }
        }
        setIntervalErr(intMsg);
        setLookbackErr(lookMsg);
        if (intMsg || lookMsg) {
            setDialogMsg('Check data properties');
            return;
        }

        // monitor cannot be active if Verity turned off the default
        if (form.active === 'YES' && !config.ActiveDefault) {
            setDialogMsg(
                `Monitor cannot be Active, Verity have turned off monitor ${config.ConfigName}. Please contact your Account Manager regarding this monitor.`,
            );
            return;
        }

        // alerts cannot be on when the monitor is off
        if (form.active === 'NO' && form.alert === 'ON') {
            setDialogMsg('Alerts must be off if Monitor is off.');
            return;
        }

        const model: MonitorDefaultSaveModel = {
            Id: config.Id,
            ConfigType: config.ConfigType,
            ConfigName: config.ConfigName,
            Active: form.active === 'YES',
            Alert: form.alert === 'ON',
            IntervalVal: parseInt(form.intervalVal, 10) || 0,
            IntervalType: form.intervalType,
            LookbackVal: parseInt(form.lookbackVal, 10) || 0,
            LookbackType: form.lookbackType,
        };

        setSaving(true);
        try {
            await datamonitorRepo.saveDatafeedConfig(model);
            setConfig(prev =>
                prev
                    ? {
                          ...prev,
                          Active: form.active === 'YES',
                          Alert: form.alert === 'ON',
                          IntervalVal: parseInt(form.intervalVal, 10) || 0,
                          IntervalType: form.intervalType,
                          LookbackVal: parseInt(form.lookbackVal, 10) || 0,
                          LookbackType: form.lookbackType,
                      }
                    : prev,
            );
            setInitialForm(form);
            setIsEditing(false);
            setSaveSuccessMsg('Value saved successfully.');
            if (saveMessageTimerRef.current) clearTimeout(saveMessageTimerRef.current);
            saveMessageTimerRef.current = setTimeout(() => {
                setSaveSuccessMsg('');
                saveMessageTimerRef.current = null;
            }, 3000);
            onSaved();
        } catch (err: any) {
            if (!utilSvc.http.isIgnore(err)) {
                setDialogMsg('Unable to save the monitor config. Please try again.');
            }
        } finally {
            setSaving(false);
        }
    }, [config, saving, showLookback, form, isDirty, onSaved]);

    const dialogActions = useMemo(() => [{ label: 'OK', onClick: (): void => setDialogMsg('') }], []);

    return (
        <>
            <FlyOutMenu
                title={title}
                isOpen={!!monitorId}
                onClose={handleClose}
                width={600}
                overflowY="auto"
                ignoreClickRef={ignoreClickRef}
            >
                {loading ? (
                    <div className="monitor-history-loading">
                        <div className="shimmer-line shimmer-line--wide" />
                        <div className="shimmer-line shimmer-line--medium" />
                    </div>
                ) : (
                    <div className="monitor-history-content">
                        <section className="monitor-info-section">
                            <h3>Information</h3>
                            <div className="info-row">
                                <div className="info-group">
                                    <label>Monitor Type:</label>
                                    <span>{monitorType}</span>
                                </div>
                            </div>

                            {config && canEdit && isEditing ? (
                                <form
                                    className="monitor-defaults-edit"
                                    onSubmit={(e): void => {
                                        e.preventDefault();
                                        handleSave();
                                    }}
                                >
                                    <div className="monitor-defaults-edit-primary-row">
                                        <div className="monitor-defaults-edit-field">
                                            <span className="monitor-defaults-edit-label">Active</span>
                                            <div className="monitor-defaults-edit-radios">
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="cfgActive"
                                                        checked={form.active === 'YES'}
                                                        onChange={(): void => update('active', 'YES')}
                                                    />
                                                    &nbsp;Yes
                                                </label>
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="cfgActive"
                                                        checked={form.active === 'NO'}
                                                        onChange={(): void => update('active', 'NO')}
                                                    />
                                                    &nbsp;No
                                                </label>
                                            </div>
                                        </div>

                                        <div className="monitor-defaults-edit-field">
                                            <span className="monitor-defaults-edit-label">Alerts</span>
                                            <div className="monitor-defaults-edit-radios">
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="cfgAlert"
                                                        checked={form.alert === 'ON'}
                                                        onChange={(): void => update('alert', 'ON')}
                                                    />
                                                    &nbsp;On
                                                </label>
                                                <label>
                                                    <input
                                                        type="radio"
                                                        name="cfgAlert"
                                                        checked={form.alert === 'OFF'}
                                                        onChange={(): void => update('alert', 'OFF')}
                                                    />
                                                    &nbsp;Off
                                                </label>
                                            </div>
                                        </div>

                                        <div className="monitor-defaults-edit-field">
                                            <span className="monitor-defaults-edit-label">Fails After</span>
                                            <div className="monitor-defaults-edit-interval">
                                                <input
                                                    type="text"
                                                    inputMode="numeric"
                                                    maxLength={10}
                                                    placeholder="number"
                                                    value={form.intervalVal}
                                                    onChange={(e): void => update('intervalVal', e.target.value)}
                                                />
                                                <select
                                                    value={form.intervalType}
                                                    onChange={(e): void => update('intervalType', e.target.value)}
                                                >
                                                    {INTERVAL_TYPES.map(t => (
                                                        <option
                                                            key={t.value}
                                                            value={t.value}
                                                        >
                                                            {t.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            {intervalErr ? (
                                                <span className="monitor-defaults-edit-err">{intervalErr}</span>
                                            ) : null}
                                        </div>
                                    </div>

                                    {showLookback ? (
                                        <div className="monitor-defaults-edit-field">
                                            <span className="monitor-defaults-edit-label">Lookback Range</span>
                                            <div className="monitor-defaults-edit-interval">
                                                <input
                                                    type="text"
                                                    inputMode="numeric"
                                                    maxLength={10}
                                                    placeholder="number"
                                                    value={form.lookbackVal}
                                                    onChange={(e): void => update('lookbackVal', e.target.value)}
                                                />
                                                <select
                                                    value={form.lookbackType}
                                                    onChange={(e): void => update('lookbackType', e.target.value)}
                                                >
                                                    {INTERVAL_TYPES.map(t => (
                                                        <option
                                                            key={t.value}
                                                            value={t.value}
                                                        >
                                                            {t.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            {lookbackErr ? (
                                                <span className="monitor-defaults-edit-err">{lookbackErr}</span>
                                            ) : null}
                                        </div>
                                    ) : null}

                                    <div className="monitor-defaults-edit-actions">
                                        <Button
                                            id="monitorConfigSaveBtn"
                                            cssName="btn-primary"
                                            onClick={handleSave}
                                            disabled={saving || !isDirty}
                                        >
                                            Save
                                        </Button>
                                        <Button
                                            id="monitorConfigCancelBtn"
                                            cssName="btn-secondary"
                                            onClick={handleCancelEdit}
                                            disabled={saving}
                                        >
                                            Cancel
                                        </Button>
                                    </div>
                                </form>
                            ) : config ? (
                                <div className="info-row">
                                    <div className="info-group">
                                        <label>Monitor Rule</label>
                                        <span>{config.Active ? 'ON' : 'OFF'}</span>
                                    </div>
                                    <div className="info-group">
                                        <label>Alerts</label>
                                        <span>{config.Alert ? 'ON' : 'OFF'}</span>
                                    </div>
                                    <div className="info-group">
                                        <label>Fails After</label>
                                        <span>
                                            {config.IntervalVal} {config.IntervalType.toUpperCase()}S
                                        </span>
                                    </div>
                                    {showLookback ? (
                                        <div className="info-group">
                                            <label>Lookback</label>
                                            <span>
                                                {config.LookbackVal} {config.LookbackType}s
                                            </span>
                                        </div>
                                    ) : null}
                                </div>
                            ) : null}
                            {config && canEdit && !isEditing ? (
                                <div className="monitor-defaults-edit-actions">
                                    <Button
                                        id="monitorConfigEditBtn"
                                        cssName="btn-secondary"
                                        onClick={handleEdit}
                                    >
                                        <FontAwesomeIcon icon={faPencil} />
                                        <span>Edit Rule</span>
                                    </Button>
                                </div>
                            ) : null}
                            {saveSuccessMsg ? <p className="monitor-defaults-edit-success">{saveSuccessMsg}</p> : null}
                        </section>

                        <section className="monitor-history-section">
                            <h3>History</h3>
                            {history.length === 0 ? (
                                <p className="no-records">No Records Found</p>
                            ) : (
                                <>
                                    <p className="record-count">Viewing {history.length} records</p>
                                    <table className="table-simple">
                                        <thead>
                                            <tr>
                                                <td style={{ width: 60 }}>Status</td>
                                                <td style={{ width: '25%' }}>Elapsed</td>
                                                <td style={{ width: '20%' }}>Changed</td>
                                                <td style={{ width: '20%' }}>Last Activity</td>
                                                <td style={{ width: '25%' }}>Detail</td>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {history.map((item, idx) => (
                                                <tr key={idx}>
                                                    <td>
                                                        <MonitorStatusIcon status={item.Status} />
                                                    </td>
                                                    <td>{item.ElapsedTxt}</td>
                                                    <td>{formatDate(item.Changed)}</td>
                                                    <td>{formatDate(item.LastChecked)}</td>
                                                    <td className="detail-cell">{item.Details}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </>
                            )}
                        </section>
                    </div>
                )}
            </FlyOutMenu>
            <Dialog
                open={!!dialogMsg}
                setOpen={(): void => setDialogMsg('')}
                title="Cannot Save"
                message={dialogMsg}
                actions={dialogActions}
            />
        </>
    );
};

export default MonitorConfigEditFlyOut;
